import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../services/api";

export interface Notification {
  id: string;
  kind:
    | "ceremony"
    | "daily_card"
    | "deck_launch"
    | "journal_save"
    | "practitioner_join"
    | "community_pull";
  title: string;
  body: string;
  deepLink: string | null;
  readAt: string | null;
  createdAt: string;
}

interface FeedResponse {
  notifications: Notification[];
  unread_count: number;
}

async function fetchFeed(): Promise<FeedResponse> {
  const res = await apiFetch("/notifications");
  if (!res.ok) throw new Error(`notifications fetch failed: ${res.status}`);
  return res.json();
}

async function markRead(id: string): Promise<void> {
  const res = await apiFetch(`/notifications/${id}/read`, { method: "PATCH" });
  if (!res.ok) throw new Error(`mark-read failed: ${res.status}`);
}

async function markAllRead(): Promise<void> {
  const res = await apiFetch("/notifications/mark-all-read", { method: "POST" });
  if (!res.ok) throw new Error(`mark-all-read failed: ${res.status}`);
}

export function useNotifications() {
  const qc = useQueryClient();

  const query = useQuery<FeedResponse>({
    queryKey: ["notifications"],
    queryFn: fetchFeed,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const markReadMutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return {
    notifications: query.data?.notifications ?? [],
    unreadCount: query.data?.unread_count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
  };
}
